// src/storage/streakManager.ts — Forgiving Streak Manager (FR-ADHD-005)
// Non-punitive daily practice streak tracker with 48-hour grace freeze and milestone rewards.

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: string; // "YYYY-MM-DD"
  lastActiveTimestamp: number;
  isFrozen: boolean;
  frozenSinceDate?: string;
  totalDaysPracticed: number;
  unlockedMilestones: number[]; // e.g. [3, 5, 10, 20]
}

export interface ActivityResult {
  currentStreak: number;
  longestStreak: number;
  isNewDay: boolean;
  resumedFromFreeze: boolean;
  milestoneHit: number | null;
  message: string;
}

export interface StreakMilestoneInfo {
  milestone: number;
  title: string;
  subtitle: string;
  badge: string;
  rewardNote: string;
}

export const STREAK_MILESTONES = [3, 5, 10, 20];

export const MILESTONE_DEFINITIONS: Record<number, StreakMilestoneInfo> = {
  3: {
    milestone: 3,
    title: 'Coastal Explorer',
    subtitle: 'Splendid momentum, Tudor! 3 days navigating the depths.',
    badge: '🌊 3-Day Voyage',
    rewardNote: 'Awarded Victorian Brass Compass Stamp!',
  },
  5: {
    milestone: 5,
    title: 'Coral Pioneer',
    subtitle: 'Magnificent dedication! 5 unbroken days of natural discovery.',
    badge: '🪸 5-Day Voyage',
    rewardNote: 'Awarded Gilded Nautilus Stamp!',
  },
  10: {
    milestone: 10,
    title: 'Deep Voyager',
    subtitle: 'Outstanding curiosity and perseverance! 10 days exploring the deep ocean.',
    badge: '🧭 10-Day Voyage',
    rewardNote: 'Awarded Bioluminescent Abyssal Lantern!',
  },
  20: {
    milestone: 20,
    title: 'High Admiral of the Abyss',
    subtitle: 'Exceptional mastery, Tudor! 20 days of focused 11+ scholarship.',
    badge: '👑 20-Day Voyage',
    rewardNote: 'Awarded Master Naturalist Grand Laurels!',
  },
};

const STORAGE_KEY = 'clawey_streak_data_v1';

export class StreakManager {
  /** Format a Date object to local YYYY-MM-DD */
  static formatDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** Calculate calendar day difference between two YYYY-MM-DD strings */
  static getDayDifference(fromDateStr: string, toDateStr: string): number {
    const parts1 = fromDateStr.split('-').map(Number);
    const parts2 = toDateStr.split('-').map(Number);
    const y1 = parts1[0] ?? 2026;
    const m1 = parts1[1] ?? 1;
    const d1 = parts1[2] ?? 1;
    const y2 = parts2[0] ?? 2026;
    const m2 = parts2[1] ?? 1;
    const d2 = parts2[2] ?? 1;
    const date1 = new Date(y1, m1 - 1, d1);
    const date2 = new Date(y2, m2 - 1, d2);
    const diffMs = date2.getTime() - date1.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  }

  /** Default empty streak profile */
  static getDefault(): StreakData {
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastActiveDate: '',
      lastActiveTimestamp: 0,
      isFrozen: false,
      totalDaysPracticed: 0,
      unlockedMilestones: [],
    };
  }

  /** Load persistent streak data from localStorage */
  static load(): StreakData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return this.getDefault();
      return { ...this.getDefault(), ...JSON.parse(raw) };
    } catch {
      return this.getDefault();
    }
  }

  /** Persist streak data to localStorage */
  static save(data: StreakData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // Ignore quota errors in constrained environments
    }
  }

  /** Clear stored streak data (test helper) */
  static clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Check current streak status without recording practice.
   * If Tudor missed yesterday (dayDiff === 2), the streak is safely frozen under the 48h grace rule.
   */
  static checkStatus(currentDate: Date = new Date()): {
    currentStreak: number;
    longestStreak: number;
    isFrozen: boolean;
    practicedToday: boolean;
    daysInactive: number;
  } {
    const data = this.load();
    if (!data.lastActiveDate) {
      return {
        currentStreak: 0,
        longestStreak: 0,
        isFrozen: false,
        practicedToday: false,
        daysInactive: 0,
      };
    }

    const todayStr = this.formatDate(currentDate);
    const dayDiff = this.getDayDifference(data.lastActiveDate, todayStr);

    if (dayDiff === 0) {
      return {
        currentStreak: data.currentStreak,
        longestStreak: data.longestStreak,
        isFrozen: false,
        practicedToday: true,
        daysInactive: 0,
      };
    }

    if (dayDiff === 1) {
      // Practiced yesterday — streak is active and waiting for today's session
      return {
        currentStreak: data.currentStreak,
        longestStreak: data.longestStreak,
        isFrozen: false,
        practicedToday: false,
        daysInactive: 1,
      };
    }

    if (dayDiff === 2) {
      // 48-Hour Grace Window: One day missed, streak is FROZEN (not lost!)
      return {
        currentStreak: data.currentStreak,
        longestStreak: data.longestStreak,
        isFrozen: true,
        practicedToday: false,
        daysInactive: 2,
      };
    }

    // dayDiff > 2: Inactive > 48 hours
    return {
      currentStreak: 0,
      longestStreak: data.longestStreak,
      isFrozen: false,
      practicedToday: false,
      daysInactive: dayDiff,
    };
  }

  /**
   * Record practice activity for today.
   * Implements non-punitive ADHD logic:
   * 1. Same-day practice: doesn't duplicate streak count.
   * 2. Consecutive day: increments streak.
   * 3. 48-hour grace window (missed 1 day): unfreezes and resumes streak!
   * 4. Inactivity > 48 hours: begins fresh streak at 1, with ZERO guilt or loss notifications.
   */
  static recordActivity(currentDate: Date = new Date()): ActivityResult {
    const data = this.load();
    const todayStr = this.formatDate(currentDate);
    const timestamp = currentDate.getTime();

    // First ever session
    if (!data.lastActiveDate) {
      data.currentStreak = 1;
      data.longestStreak = 1;
      data.lastActiveDate = todayStr;
      data.lastActiveTimestamp = timestamp;
      data.isFrozen = false;
      data.totalDaysPracticed = 1;
      this.save(data);

      return {
        currentStreak: 1,
        longestStreak: 1,
        isNewDay: true,
        resumedFromFreeze: false,
        milestoneHit: null,
        message: 'Welcome to your first expedition, Tudor! The marine journey begins!',
      };
    }

    const dayDiff = this.getDayDifference(data.lastActiveDate, todayStr);

    // 1. Already practiced today
    if (dayDiff === 0) {
      return {
        currentStreak: data.currentStreak,
        longestStreak: data.longestStreak,
        isNewDay: false,
        resumedFromFreeze: false,
        milestoneHit: null,
        message: `Steady progress today, Tudor! Current streak: ${data.currentStreak} days.`,
      };
    }

    let resumedFromFreeze = false;
    let milestoneHit: number | null = null;

    if (dayDiff === 1) {
      // 2. Consecutive day
      data.currentStreak += 1;
      data.isFrozen = false;
      data.frozenSinceDate = undefined;
    } else if (dayDiff === 2) {
      // 3. 48-Hour Grace Pause: Missed 1 day, resuming unfreezes and advances the streak!
      data.currentStreak += 1;
      data.isFrozen = false;
      data.frozenSinceDate = undefined;
      resumedFromFreeze = true;
    } else {
      // 4. Inactive > 48 hours: Restart fresh without shame/loss penalty
      data.currentStreak = 1;
      data.isFrozen = false;
      data.frozenSinceDate = undefined;
    }

    data.lastActiveDate = todayStr;
    data.lastActiveTimestamp = timestamp;
    data.totalDaysPracticed += 1;
    data.longestStreak = Math.max(data.longestStreak, data.currentStreak);

    // Check for newly achieved milestone
    if (STREAK_MILESTONES.includes(data.currentStreak) && !data.unlockedMilestones.includes(data.currentStreak)) {
      data.unlockedMilestones.push(data.currentStreak);
      milestoneHit = data.currentStreak;
    }

    this.save(data);

    let message = `Splendid work, Tudor! Your voyage continues at ${data.currentStreak} day${data.currentStreak > 1 ? 's' : ''}!`;
    if (resumedFromFreeze) {
      message = `⚓ Welcome back, Tudor! Your 48-hour grace pause kept your streak safe at ${data.currentStreak} days!`;
    }

    return {
      currentStreak: data.currentStreak,
      longestStreak: data.longestStreak,
      isNewDay: true,
      resumedFromFreeze,
      milestoneHit,
      message,
    };
  }

  /**
   * Helper to retrieve encouraging Victorian Naturalist feedback.
   * Guarantees zero negative/loss phrasing.
   */
  static getEncouragingMessage(status = this.checkStatus()): string {
    if (status.practicedToday) {
      return `🌟 Today's dive is logged! You're sailing on a ${status.currentStreak}-day voyage.`;
    }
    if (status.isFrozen) {
      return `⚓ Your streak is safely anchored in the 48-hour lagoon! Complete any practice today to sail on.`;
    }
    if (status.currentStreak > 0) {
      return `🌊 Clawey is ready at the helm! Dive in today to reach day ${status.currentStreak + 1}.`;
    }
    return `🐚 Ready for today's ocean exploration, Tudor? Every dive builds your Bexley stamina!`;
  }
}
